import { ViewerApi } from 'Data/Viewer.api';
import { ViewerEngineEnum } from 'Data/enums/ViewerEngine';
import { IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { IIfcArtifacts, toIfcArtifacts } from 'Data/interfaces/Viewer/IIfcArtifacts';
import {
  INativeViewerArtifacts,
  hasReadyNativeArtifacts,
} from 'Data/interfaces/Viewer/INativeViewerArtifacts';

// Um parse frio leva de segundos a dezenas de minutos (fila serial no backend);
// 5 s é o bastante para a disciplina entrar logo depois de ficar pronta.
const POLL_MS = 5 * 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `artifacts: null` com `failure` = o modelo não tem como entrar; sem `failure`
 * = espera cancelada (switch desligado, viewer desmontado), que não é falha.
 * `waited`: houve conversão no backend durante a espera.
 */
export interface INativeModelResult {
  artifacts: IIfcArtifacts | null;
  waited: boolean;
  failure?: { reason: string; failureCode?: string | null };
}

/**
 * Artefatos prontos de UM arquivo da federação: pede o render nativo quando
 * ainda não existe (ou falhou) e espera o backend terminar.
 *
 * Sem artefatos quando o modelo não tem como entrar na sessão IFC — o backend
 * rebaixou para a Autodesk (que não desenha nesta cena), o render falhou, ou
 * `isActive` virou falso (switch desligado, viewer desmontado) durante a espera.
 */
export const prepareNativeModel = async (
  file: IFileData,
  constructionSiteId: number,
  isActive: () => boolean,
): Promise<INativeModelResult> => {
  const fileApiId = file.Identifier?.ApiId || file.Identifier?.ItemId;
  if (!fileApiId) { return { artifacts: null, waited: false, failure: { reason: 'no-file-id' } }; }

  const fetchArtifacts = async (): Promise<INativeViewerArtifacts> => (
    (await ViewerApi.getNativeArtifacts({ constructionSiteId, fileApiId })).data.data
  );

  let current = await fetchArtifacts();
  let waited = current.Status !== 'ready';

  // Nunca foi nativo, ou falhou antes: pede de novo. Diferente do viewer de
  // arquivo único, aqui não há Autodesk para cair — a sessão é IFC.
  if (current.Status === 'not_found' || current.Status === 'failed') {
    await ViewerApi.renderViewerFile({
      constructionSiteId,
      fileApiId,
      folderId: '',
      engine: ViewerEngineEnum.Native,
    });
    current = await fetchArtifacts();
    // A resposta pode vir sem `engine`: quem diz se virou nativo é o endpoint
    // de artefatos. Continuou `not_found` = o backend rebaixou.
    if (current.Status === 'not_found') {
      return { artifacts: null, waited, failure: { reason: 'native-downgraded' } };
    }
  }

  while (!hasReadyNativeArtifacts(current)) {
    if (current.Status === 'failed') {
      return { artifacts: null, waited, failure: { reason: 'native-failed', failureCode: current.FailureCode } };
    }
    if (current.Status === 'not_found') {
      return { artifacts: null, waited, failure: { reason: 'native-not-found' } };
    }
    waited = true;
    await wait(POLL_MS);
    if (!isActive()) {
      return { artifacts: null, waited };
    }
    current = await fetchArtifacts();
  }

  return { artifacts: isActive() ? toIfcArtifacts(current) : null, waited };
};
